/*
 **	Alarm Resolve Modal Component
 **	OMNI_IQ Mobile (c) 2025 Silver Falls Capital. All Rights Reserved
 */

import React, { useState } from 'react'
import { IOSSlideUpPanel } from './ui/IOSSlideUpPanel'
import { OCAColors, Typography, Spacing, BorderRadius } from '../theme/colors'
import type { AlarmResolution } from '../services/api'

interface AlarmResolveModalProps {
  isOpen: boolean
  onClose: () => void
  escalationId: string
  deviceName: string
  onSubmit: (resolution: AlarmResolution) => Promise<void>
}

export const AlarmResolveModal: React.FC<AlarmResolveModalProps> = ({
  isOpen,
  onClose,
  escalationId,
  deviceName,
  onSubmit
}) => {
  const [problemDescription, setProblemDescription] = useState('')
  const [resolutionDescription, setResolutionDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async () => {
    // Validation
    if (problemDescription.trim().length < 10) {
      setError('Problem description must be at least 10 characters')
      return
    }
    if (resolutionDescription.trim().length < 10) {
      setError('Resolution description must be at least 10 characters')
      return
    }

    setError('')
    setIsSubmitting(true)

    try {
      await onSubmit({
        problemDescription: problemDescription.trim(),
        resolutionDescription: resolutionDescription.trim()
      })

      // Reset form and close modal
      setProblemDescription('')
      setResolutionDescription('')
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resolve alarm')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setProblemDescription('')
      setResolutionDescription('')
      setError('')
      onClose()
    }
  }

  const isValid = problemDescription.trim().length >= 10 && resolutionDescription.trim().length >= 10

  return (
    <IOSSlideUpPanel
      isOpen={isOpen}
      onClose={handleClose}
      title="Resolve Alarm"
      height="85vh"
    >
      <div style={{
        padding: `${Spacing.md} ${Spacing.lg}`,
        display: 'flex',
        flexDirection: 'column',
        gap: Spacing.lg,
        height: '100%',
      }}>
        {/* Device Info */}
        <div style={{
          padding: Spacing.md,
          backgroundColor: OCAColors.background,
          borderRadius: BorderRadius.md,
          border: `1px solid ${OCAColors.separator}`,
        }}>
          <div style={{
            fontSize: Typography.fontSize.sm,
            color: OCAColors.secondaryLabel,
            marginBottom: '4px',
          }}>
            Device
          </div>
          <div style={{
            fontSize: Typography.fontSize.base,
            fontWeight: Typography.fontWeight.semibold,
            color: OCAColors.label,
          }}>
            {deviceName}
          </div>
          <div style={{
            fontSize: Typography.fontSize.xs,
            color: OCAColors.tertiaryLabel,
            marginTop: '4px',
          }}>
            ID: {escalationId.substring(0, 8)}...
          </div>
        </div>

        {/* Problem Description */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: Spacing.xs,
        }}>
          <label style={{
            fontSize: Typography.fontSize.base,
            fontWeight: Typography.fontWeight.medium,
            color: OCAColors.label,
          }}>
            Problem Description *
          </label>
          <textarea
            value={problemDescription}
            onChange={(e) => setProblemDescription(e.target.value)}
            placeholder="Describe the problem that caused this alarm (minimum 10 characters)"
            disabled={isSubmitting}
            style={{
              width: '100%',
              minHeight: '120px',
              padding: Spacing.md,
              fontSize: Typography.fontSize.base,
              color: OCAColors.label,
              backgroundColor: OCAColors.background,
              border: `1px solid ${OCAColors.separator}`,
              borderRadius: BorderRadius.md,
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = OCAColors.primary
              e.target.style.boxShadow = `0 0 0 3px ${OCAColors.primary}1A`
            }}
            onBlur={(e) => {
              e.target.style.borderColor = OCAColors.separator
              e.target.style.boxShadow = 'none'
            }}
          />
          <div style={{
            fontSize: Typography.fontSize.xs,
            color: problemDescription.trim().length >= 10 ? OCAColors.success : OCAColors.tertiaryLabel,
          }}>
            {problemDescription.trim().length} / 10 characters minimum
          </div>
        </div>

        {/* Resolution Description */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: Spacing.xs,
        }}>
          <label style={{
            fontSize: Typography.fontSize.base,
            fontWeight: Typography.fontWeight.medium,
            color: OCAColors.label,
          }}>
            Resolution Description *
          </label>
          <textarea
            value={resolutionDescription}
            onChange={(e) => setResolutionDescription(e.target.value)}
            placeholder="Describe how the problem was resolved (minimum 10 characters)"
            disabled={isSubmitting}
            style={{
              width: '100%',
              minHeight: '120px',
              padding: Spacing.md,
              fontSize: Typography.fontSize.base,
              color: OCAColors.label,
              backgroundColor: OCAColors.background,
              border: `1px solid ${OCAColors.separator}`,
              borderRadius: BorderRadius.md,
              fontFamily: 'inherit',
              resize: 'vertical',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = OCAColors.primary
              e.target.style.boxShadow = `0 0 0 3px ${OCAColors.primary}1A`
            }}
            onBlur={(e) => {
              e.target.style.borderColor = OCAColors.separator
              e.target.style.boxShadow = 'none'
            }}
          />
          <div style={{
            fontSize: Typography.fontSize.xs,
            color: resolutionDescription.trim().length >= 10 ? OCAColors.success : OCAColors.tertiaryLabel,
          }}>
            {resolutionDescription.trim().length} / 10 characters minimum
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            padding: Spacing.md,
            backgroundColor: `${OCAColors.danger}15`,
            border: `1px solid ${OCAColors.danger}`,
            borderRadius: BorderRadius.md,
            color: OCAColors.danger,
            fontSize: Typography.fontSize.sm,
          }}>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: Spacing.md,
          marginTop: 'auto',
          paddingTop: Spacing.md,
        }}>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            style={{
              flex: 1,
              padding: `${Spacing.md} ${Spacing.lg}`,
              fontSize: Typography.fontSize.base,
              fontWeight: Typography.fontWeight.semibold,
              color: OCAColors.primary,
              backgroundColor: 'transparent',
              border: `1.5px solid ${OCAColors.primary}`,
              borderRadius: BorderRadius.md,
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              opacity: isSubmitting ? 0.5 : 1,
              transition: 'all 0.2s ease',
            }}
            onMouseDown={(e) => {
              if (!isSubmitting) {
                e.currentTarget.style.transform = 'scale(0.97)'
              }
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
            style={{
              flex: 1,
              padding: `${Spacing.md} ${Spacing.lg}`,
              fontSize: Typography.fontSize.base,
              fontWeight: Typography.fontWeight.semibold,
              color: '#FFFFFF',
              backgroundColor: isValid && !isSubmitting ? OCAColors.primary : OCAColors.tertiaryLabel,
              border: 'none',
              borderRadius: BorderRadius.md,
              cursor: isValid && !isSubmitting ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
            }}
            onMouseDown={(e) => {
              if (isValid && !isSubmitting) {
                e.currentTarget.style.transform = 'scale(0.97)'
              }
            }}
            onMouseUp={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)'
            }}
          >
            {isSubmitting ? 'Resolving...' : 'Resolve Alarm'}
          </button>
        </div>

        {/* Info Message - placed after buttons so they're always visible */}
        <div style={{
          padding: Spacing.md,
          backgroundColor: `${OCAColors.primary}10`,
          border: `1px solid ${OCAColors.primary}30`,
          borderRadius: BorderRadius.md,
          fontSize: Typography.fontSize.sm,
          color: OCAColors.secondaryLabel,
        }}>
          <strong>Note:</strong> Resolving this alarm will automatically acknowledge it if not already acknowledged.
        </div>
      </div>
    </IOSSlideUpPanel>
  )
}
